import { ResponsiveTable } from './components/common/ResponsiveTable';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, getAuthHeaders } from './config/api';
import { formatDate } from './utils/dateFormat';
import { BolivianDateInput } from './components/common/BolivianDateInput';

export default function MedicalStoryList({ token, patientId }: { token: string, patientId: number }) {
  const { t } = useTranslation();
  const [stories, setStories] = useState<any[]>([]);
  const [form, setForm] = useState({ date: '', description: '', appointmentId: '' });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchStories = () => {
    setLoading(true);
    fetch(getApiUrl(API_CONFIG.ENDPOINTS.PATIENT_MEDICAL_STORIES, { patientId }), {
      headers: getAuthHeaders(token)
    })
      .then(res => res.json())
      .then(setStories)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  // Fetching on token/patient change is intentional.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchStories(); }, [token, patientId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.MEDICAL_STORIES), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ ...form, patientId, appointmentId: form.appointmentId ? parseInt(form.appointmentId) : undefined })
      });
      if (!res.ok) throw new Error(t('medicalStories.createFailed'));
      setForm({ date: '', description: '', appointmentId: '' });
      fetchStories();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (s: any) => {
    setEditingId(s.id);
    setForm({ date: s.date?.slice(0, 10), description: s.description, appointmentId: s.appointmentId || '' });
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.MEDICAL_STORIES}/${editingId}`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ ...form, patientId, appointmentId: form.appointmentId ? parseInt(form.appointmentId) : undefined })
      });
      if (!res.ok) throw new Error(t('medicalStories.updateFailed'));
      setEditingId(null);
      setForm({ date: '', description: '', appointmentId: '' });
      fetchStories();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('medicalStories.confirmDelete'))) return;
    setError('');
    try {
      const res = await fetch(getApiUrl(`${API_CONFIG.ENDPOINTS.MEDICAL_STORIES}/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!res.ok) throw new Error(t('medicalStories.deleteFailed'));
      fetchStories();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <div>{t('medicalStories.loading')}</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div>
      <h3>{t('medicalStories.title')}</h3>
      <form className="responsive-row" onSubmit={editingId ? handleUpdate : handleSubmit} style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <BolivianDateInput name="date" value={form.date} onChange={date => setForm(current => ({ ...current, date }))} required style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <input name="appointmentId" type="number" placeholder={t('medicalStories.appointmentIdOptional')} value={form.appointmentId} onChange={handleChange} style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9' }} />
        <textarea name="description" placeholder={t('medicalStories.description')} value={form.description} onChange={handleChange} required style={{ padding: '6px', border: '1px solid #334155', borderRadius: 4, background: '#0f172a', color: '#f1f5f9', minWidth: 300 }} />
        <button type="submit" style={{ padding: '6px 12px', background: '#1976d2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{editingId ? t('medicalStories.update') : t('medicalStories.add')} {t('medicalStories.story')}</button>
        {editingId && <button type="button" onClick={() => { setEditingId(null); setForm({ date: '', description: '', appointmentId: '' }); }} style={{ padding: '6px 12px', background: '#64748b', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('medicalStories.cancel')}</button>}
      </form>
      <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#1e293b', color: '#f1f5f9' }}>
            <th style={{ padding: 8, textAlign: 'left' }}>{t('medicalStories.date')}</th>
            <th style={{ padding: 8, textAlign: 'left' }}>{t('medicalStories.appointmentId')}</th>
            <th style={{ padding: 8, textAlign: 'left' }}>{t('medicalStories.description')}</th>
            <th style={{ padding: 8, textAlign: 'left' }}>{t('medicalStories.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {stories.map(s => (
            <tr key={s.id} style={{ borderBottom: '1px solid #334155', color: '#f1f5f9' }}>
              <td style={{ padding: 8 }}>{formatDate(s.date)}</td>
              <td style={{ padding: 8 }}>{s.appointmentId}</td>
              <td style={{ padding: 8 }}>{s.description}</td>
              <td style={{ padding: 8 }}>
                <button onClick={() => handleEdit(s)} style={{ marginRight: 8, padding: '4px 8px', cursor: 'pointer' }}>{t('medicalStories.edit')}</button>
                <button onClick={() => handleDelete(s.id)} style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}>{t('medicalStories.delete')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></ResponsiveTable>
    </div>
  );
}
